import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useState,
  useSyncExternalStore,
} from 'react';
import { getSettleState, subscribeSettleState } from '@/common/queries/itemsTreeSetSettleState';

export type SettleMark = 'pending' | 'settled';

type SettleMarksStore = {
  get: (id: number) => SettleMark | undefined;
  subscribe: (id: number, listener: () => void) => () => void;
  sync: (scope: string) => void;
};

const createSettleMarksStore = (): SettleMarksStore => {
  let marks: ReadonlyMap<number, SettleMark> = new Map();
  const listenersById = new Map<number, Set<() => void>>();

  return {
    get: (id) => marks.get(id),
    subscribe: (id, listener) => {
      let listeners = listenersById.get(id);
      if (!listeners) {
        listeners = new Set();
        listenersById.set(id, listeners);
      }
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) listenersById.delete(id);
      };
    },
    sync: (scope) => {
      const { pendingIds, settledIds } = getSettleState(scope);
      const next = new Map<number, SettleMark>();
      for (const id of settledIds) next.set(id, 'settled');
      for (const id of pendingIds) next.set(id, 'pending');

      const previous = marks;
      marks = next;
      const changedIds = new Set([...previous.keys(), ...next.keys()]);
      for (const id of changedIds) {
        if (previous.get(id) === next.get(id)) continue;
        for (const listener of listenersById.get(id) ?? []) listener();
      }
    },
  };
};

const SettleMarksContext = createContext<SettleMarksStore | null>(null);

export function SettleMarksProvider({
  enabled,
  children,
}: {
  enabled: boolean;
  children: ReactNode;
}) {
  const scope = useId();
  const [store] = useState(createSettleMarksStore);

  useEffect(() => {
    if (!enabled) return;
    const unsubscribe = subscribeSettleState(scope, () => store.sync(scope));
    store.sync(scope);
    return () => {
      unsubscribe();
      store.sync(scope);
    };
  }, [enabled, scope, store]);

  return <SettleMarksContext.Provider value={store}>{children}</SettleMarksContext.Provider>;
}

const NO_UNSUBSCRIBE = () => {};
const NO_MARK = () => undefined;

export function useSettleMark(itemId: number): SettleMark | undefined {
  const store = useContext(SettleMarksContext);
  const subscribe = useCallback(
    (listener: () => void) => (store ? store.subscribe(itemId, listener) : NO_UNSUBSCRIBE),
    [store, itemId],
  );
  const getSnapshot = useCallback(() => (store ? store.get(itemId) : NO_MARK()), [store, itemId]);
  return useSyncExternalStore(subscribe, getSnapshot);
}
